import { DateValue } from "@mantine/dates"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FilterChipData } from "../odoo/filter-chip"

export type BackOrderFilters = {
  purchaseCode?: string
  createdAt: [DateValue, DateValue]
  purchaseType?: string | null
  senderBusinessPartnerRefCodeBusinessName?: string
  senderBusinessRefCodeProfileName?: string
  receiverBusinessPartnerRefCodeBusinessName?: string
  receiverBusinessRefCodeProfileName?: string
  orderStatus?: string | null
  deliveryDate: [DateValue, DateValue]
  deliveryNoteStatus?: string | null
  deliveryNoteStaffFirstNameEmail?: string
  receiverBranchName?: string
  receiverStaffLastNameFirstName?: string
  orderAmount: [string | number | null, string | number | null]
  paidAmount: [string | number | null, string | number | null]
  amountToPay: [string | number | null, string | number | null]
  paymentStatus?: null | string
  invoiceRefCode?: string
  invoiceStatus?: string | null
  paymentTermDescription?: string
  orderedUserLastNameFirstName?: string
  searchQuery?: string
}

const initialFilter: BackOrderFilters = {
  purchaseCode: "",
  createdAt: [null, null],
  purchaseType: null,
  senderBusinessPartnerRefCodeBusinessName: "",
  senderBusinessRefCodeProfileName: "",
  receiverBusinessPartnerRefCodeBusinessName: "",
  receiverBusinessRefCodeProfileName: "",
  orderStatus: null,
  deliveryDate: [null, null],
  deliveryNoteStatus: null,
  deliveryNoteStaffFirstNameEmail: "",
  receiverBranchName: "",
  receiverStaffLastNameFirstName: "",
  orderAmount: [null, null],
  paidAmount: [null, null],
  amountToPay: [null, null],
  paymentStatus: null,
  invoiceRefCode: "",
  invoiceStatus: null,
  paymentTermDescription: "",
  orderedUserLastNameFirstName: "",
  searchQuery: "",
}

type FilterLabelMap = {
  key: keyof BackOrderFilters
  labelKey: string
  format?: (val: any) => string | null
}

const FILTER_LABEL_KEYS: FilterLabelMap[] = [
  { key: "purchaseCode", labelKey: "order:backOrder.poNo" },
  { key: "purchaseType", labelKey: "order:backOrder.poType" },
  { key: "orderStatus", labelKey: "order:backOrder.poStatus" },
  { key: "paymentStatus", labelKey: "order:backOrder.paymentStatus" },
  { key: "deliveryNoteStatus", labelKey: "order:backOrder.deliveryStatus" },
  { key: "invoiceStatus", labelKey: "order:backOrder.invoiceStatus" },
  { key: "invoiceRefCode", labelKey: "order:backOrder.invoiceNo" },
  { key: "receiverBranchName", labelKey: "order:backOrder.receiverBranch" },
  { key: "senderBusinessPartnerRefCodeBusinessName", labelKey: "order:backOrder.buyer" },
  { key: "receiverBusinessPartnerRefCodeBusinessName", labelKey: "order:backOrder.supplierName" },
  { key: "orderedUserLastNameFirstName", labelKey: "order:backOrder.poCreatedBy" },
  { key: "paymentTermDescription", labelKey: "order:backOrder.paymentTerm" },
  { key: "searchQuery", labelKey: "common:actions.search" },
  {
    key: "createdAt",
    labelKey: "order:backOrder.poDate",
    format: (val: [DateValue, DateValue]) => {
      if (!val || (!val[0] && !val[1])) return null
      return `${val[0] || "..."} - ${val[1] || "..."}`
    },
  },
  {
    key: "deliveryDate",
    labelKey: "order:backOrder.deliveryDate",
    format: (val: [DateValue, DateValue]) => {
      if (!val || (!val[0] && !val[1])) return null
      return `${val[0] || "..."} - ${val[1] || "..."}`
    },
  },
]

function getDisplayValue(val: any): string | null {
  if (val === null || val === undefined || val === "") return null
  if (Array.isArray(val)) {
    if (val.every((v) => v === null || v === undefined || v === "")) return null
    return val.filter(Boolean).join(" - ")
  }
  return String(val)
}

export function useBackOrderFilters() {
  const { t } = useTranslation(["order", "common"])
  const [filters, setFilters] = useState<BackOrderFilters>(structuredClone(initialFilter))

  const setFilter = <K extends keyof BackOrderFilters>(key: K, value: BackOrderFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters(structuredClone(initialFilter))
  }

  const removeFilter = (key: keyof BackOrderFilters) => {
    setFilters((prev) => ({ ...prev, [key]: (initialFilter as any)[key] }))
  }

  const activeFilterChips: FilterChipData[] = FILTER_LABEL_KEYS.reduce<FilterChipData[]>((chips, item) => {
    const val = filters[item.key]
    const display = item.format ? item.format(val) : getDisplayValue(val)
    if (display) {
      chips.push({
        key: item.key,
        label: t(item.labelKey),
        value: display,
        onRemove: () => removeFilter(item.key),
      })
    }
    return chips
  }, [])

  const { searchQuery, ...apiFilters } = filters

  return { filters: apiFilters, setFilters, setFilter, clearFilters, removeFilter, activeFilterChips, searchQuery }
}
